import { ChevronLeft, ChevronRight } from "lucide-react"

import { Boton } from "@/componentes/ui/Boton"

/** El mes elegido, o `null` cuando se miran todos. */
export type Periodo = { anio: number; mes: number } | null

const MESES = [
  "enero",
  "febrero",
  "marzo",
  "abril",
  "mayo",
  "junio",
  "julio",
  "agosto",
  "septiembre",
  "octubre",
  "noviembre",
  "diciembre",
]

/** El mes en curso, que es con el que arranca quien no elige nada. */
export function mesActual(): Periodo {
  const d = new Date()
  return { anio: d.getFullYear(), mes: d.getMonth() + 1 }
}

/**
 * Las dos fechas que el servidor entiende, en `AAAA-MM-DD`.
 *
 * ⚠ **El último día se calcula con el día 0 del mes SIGUIENTE**, que es como se pide
 * el último de cualquier mes sin tener que saber cuál es: 28, 29, 30 o 31. Escribir 30
 * a mano deja fuera el 31 de los meses largos y adelanta febrero dos días.
 */
export function limitesDe(periodo: Periodo): { desde: string; hasta: string } | null {
  if (periodo === null) return null
  const dos = (n: number) => String(n).padStart(2, "0")
  const ultimo = new Date(periodo.anio, periodo.mes, 0).getDate()
  return {
    desde: `${periodo.anio}-${dos(periodo.mes)}-01`,
    hasta: `${periodo.anio}-${dos(periodo.mes)}-${dos(ultimo)}`,
  }
}

export function nombreDe(periodo: Periodo): string {
  return periodo === null ? "Todos los períodos" : `${MESES[periodo.mes - 1]} de ${periodo.anio}`
}

/**
 * Elegir el mes que se mira, con flechas y un «todos».
 *
 * ⚠ **Es el filtro PRINCIPAL de una lista que crece sin parar**, y por eso va
 * primero: un extracto o una bandeja de órdenes se consultan por mes, no por «las
 * últimas doscientas». Sin él, la única manera de llegar a agosto es bajar hasta
 * agosto.
 *
 * ⚠ **No se puede avanzar más allá del mes en curso.** Los meses futuros están
 * siempre vacíos, y dejar pasar a diciembre de 2027 sólo sirve para que alguien crea
 * que se perdieron los datos.
 */
export function SelectorPeriodo({
  valor,
  onCambiar,
  etiqueta = "Período",
}: {
  valor: Periodo
  onCambiar: (periodo: Periodo) => void
  etiqueta?: string
}) {
  const hoy = new Date()
  const enElPresente =
    valor !== null && valor.anio === hoy.getFullYear() && valor.mes === hoy.getMonth() + 1

  function mover(pasos: number) {
    if (valor === null) return
    const d = new Date(valor.anio, valor.mes - 1 + pasos, 1)
    if (d > hoy) return
    onCambiar({ anio: d.getFullYear(), mes: d.getMonth() + 1 })
  }

  return (
    // `fieldset` y no un `div` con `role="group"`: el elemento semántico ya ES el
    // grupo, y la leyenda lo nombra sin un `aria-label` que mantener aparte.
    <fieldset className="flex items-center gap-1">
      <legend className="sr-only">{etiqueta}</legend>
      <Boton
        tamano="sm"
        variante="fantasma"
        onClick={() => mover(-1)}
        disabled={valor === null}
        aria-label="Mes anterior"
      >
        <ChevronLeft size={16} aria-hidden={true} />
      </Boton>
      <span className="min-w-[9.5rem] text-center text-sm font-medium">{nombreDe(valor)}</span>
      <Boton
        tamano="sm"
        variante="fantasma"
        onClick={() => mover(1)}
        disabled={valor === null || enElPresente}
        aria-label="Mes siguiente"
      >
        <ChevronRight size={16} aria-hidden={true} />
      </Boton>
      {/* ⚠ «Todos» quita el filtro y deja la lista con su tope y su «cargar más». No
          es lo mismo que un mes muy ancho: sirve para buscar algo cuya fecha no se
          recuerda, que es cuando el filtro por mes estorba. */}
      <Boton
        tamano="sm"
        variante={valor === null ? "primario" : "fantasma"}
        onClick={() => onCambiar(valor === null ? mesActual() : null)}
      >
        {valor === null ? "Ver por mes" : "Todos"}
      </Boton>
    </fieldset>
  )
}
